import {
  type ButtonHTMLAttributes,
  type ReactNode,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dropdown, Tooltip } from "antd";
import { DownOutlined, EllipsisOutlined } from "@ant-design/icons";

type TabItem = {
  key: string;
  label: ReactNode;
} & Record<string, unknown>;

const truncateLabel = (label: ReactNode, maxLabelChars: number): ReactNode => {
  if (typeof label !== "string" || label.length <= maxLabelChars) {
    return label;
  }
  return `${label.slice(0, maxLabelChars - 1)}…`;
};

export const TabsSwitcher = ({
  tabItems,
  activeTab,
  setActiveTab,
  maxVisibleTabs = 5,
  maxLabelChars = 50,
}: {
  tabItems: TabItem[];
  activeTab?: TabItem;
  setActiveTab: (value: any) => void;
  maxVisibleTabs?: number;
  maxLabelChars?: number;
}) => {
  const visibleTabs = tabItems.slice(0, maxVisibleTabs);
  const overflowTabs = tabItems.slice(maxVisibleTabs);
  const activeOverflowTab = overflowTabs.find(
    (tab) => tab.key === activeTab?.key,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [highlightStyle, setHighlightStyle] = useState<{
    width: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const activeButton = container.querySelector<HTMLButtonElement>(
        '[data-active="true"]',
      );
      if (!activeButton) {
        setHighlightStyle(null);
        return;
      }
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setHighlightStyle({
        width: activeButton.offsetWidth,
        left: activeButton.offsetLeft,
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [activeTab?.key, visibleTabs.length]);

  return (
    <div
      ref={containerRef}
      className="relative flex overflow-hidden rounded-[4px] border border-[rgba(0, 0, 0, 0.12)] bg-white p-1"
    >
      {highlightStyle && (
        <div
          className="absolute top-1 bottom-1 transition-[transform,width] duration-300 ease-out"
          style={{
            width: `${highlightStyle.width}px`,
            transform: `translateX(${highlightStyle.left - 4}px)`,
          }}
          aria-hidden
        >
          <div className="h-full w-full rounded-[4px] bg-[#075770]" />
        </div>
      )}
      {visibleTabs.map((item) => (
        <TabButton
          key={item.key}
          isActive={activeTab?.key === item.key}
          onClick={() => setActiveTab(item)}
          label={item.label}
          maxLabelChars={maxLabelChars}
        />
      ))}
      {overflowTabs.length > 0 && (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: overflowTabs.map((item) => ({
              key: item.key,
              label:
                typeof item.label === "string" ? (
                  <Tooltip title={item.label}>
                    <span>{truncateLabel(item.label, maxLabelChars)}</span>
                  </Tooltip>
                ) : (
                  item.label
                ),
              onClick: () => setActiveTab(item),
            })),
          }}
        >
          <OverflowTabButton
            activeTab={activeOverflowTab}
            maxLabelChars={maxLabelChars}
          />
        </Dropdown>
      )}
    </div>
  );
};

const TabButton = ({
  isActive,
  onClick,
  label,
  maxLabelChars,
}: {
  isActive: boolean;
  onClick: () => void;
  label: ReactNode;
  maxLabelChars: number;
}) => {
  const button = (
    <button
      data-active={isActive}
      className={`relative z-10 whitespace-nowrap px-3 text-center text-[14px] font-semibold transition-colors py-[6px] rounded-[4px] ${
        isActive ? "text-white" : "text-slate-700"
      }`}
      onClick={onClick}
    >
      {truncateLabel(label, maxLabelChars)}
    </button>
  );

  if (typeof label !== "string") return button;
  return <Tooltip title={label}>{button}</Tooltip>;
};

type OverflowTabButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  activeTab?: TabItem;
  maxLabelChars: number;
  ref?: Ref<HTMLButtonElement>;
};

const OverflowTabButton = ({
  activeTab,
  maxLabelChars,
  ref,
  ...triggerProps
}: OverflowTabButtonProps) => (
  <button
    {...triggerProps}
    ref={ref}
    data-active={Boolean(activeTab)}
    className={`relative z-10 flex items-center gap-1 whitespace-nowrap px-3 text-center text-[14px] font-semibold transition-colors py-[6px] rounded-[4px] ${
      activeTab ? "text-white" : "text-slate-700"
    }`}
  >
    {activeTab ? (
      <>
        {truncateLabel(activeTab.label, maxLabelChars)}
        <DownOutlined className="text-xs" />
      </>
    ) : (
      <EllipsisOutlined />
    )}
  </button>
);
